/**
 * What came back.
 *
 * A campaign screen that can only say how many messages went out is half a
 * tool. This is the other half, and it deliberately does not build a second
 * inbox: it lists the threads the shared mailbox already holds for this
 * campaign and hands the operator straight over to /email, where replying,
 * archiving, converting to a ticket and suppressing all already work.
 *
 * Empty is the normal state until sending exists, so the empty state explains
 * WHY it is empty rather than showing a shrug. The two reasons are different
 * and the operator can act on only one of them.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Inbox, MailOpen } from "lucide-react";
import { Badge, Loader } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { getThreads, type MailThread } from "../../../../lib/mail";
import { dt } from "../shared";

export function RepliesPanel({
  campaignId,
  inboundEnabled,
  replyMailbox,
  campaignReplyTo,
}: {
  campaignId: string;
  /** Whether anything polls a mailbox at all. */
  inboundEnabled: boolean;
  /** The mailbox the poller reads. */
  replyMailbox: string;
  /** Where this campaign tells people to reply. */
  campaignReplyTo: string;
}) {
  const t = useT();
  const [rows, setRows] = useState<MailThread[] | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await getThreads({ campaignId, pageSize: 100 });
      setRows(res.rows);
    } catch {
      setRows([]);
    }
  }, [campaignId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The trap this panel exists to expose: a campaign can name a reply address
  // that nothing polls, and then no reply can ever appear here no matter how
  // well the campaign performs.
  const mismatch =
    inboundEnabled &&
    !!replyMailbox &&
    !!campaignReplyTo &&
    campaignReplyTo.trim().toLowerCase() !== replyMailbox.trim().toLowerCase();

  if (rows === null) return <Loader />;

  return (
    <div className="space-y-3">
      {!inboundEnabled && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-400/40 bg-amber-50/60 px-4 py-3 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
          <AlertTriangle className="mt-px size-3.5 shrink-0" />
          {t("marketing.replies.inboundOff")}
        </p>
      )}
      {mismatch && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-400/40 bg-amber-50/60 px-4 py-3 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
          <AlertTriangle className="mt-px size-3.5 shrink-0" />
          {t("marketing.replies.mailboxMismatch", { replyTo: campaignReplyTo, mailbox: replyMailbox })}
        </p>
      )}

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border px-4 py-12 text-center text-sm text-muted-foreground">
          <Inbox className="mx-auto mb-2 size-7 opacity-40" />
          <p>{t("marketing.replies.empty")}</p>
          <Link to="/email" className="mt-2 inline-block text-xs underline underline-offset-2 hover:text-foreground">
            {t("marketing.replies.openInbox")}
          </Link>
        </div>
      ) : (
        <>
          <Badge variant="outline" className="h-8 px-3 text-xs font-normal">
            {/* `n`, not `count`: the plural chooser in i18n/message.ts only
                fires on a variable with that name, and English would otherwise
                read "1 replies". */}
            {t("marketing.replies.count", { n: rows.length })}
          </Badge>
          <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card/70">
            {rows.map((th) => {
              const last = th.messages?.[0];
              const who = th.matchedCompanyName ?? th.client?.name ?? last?.fromAddress ?? "-";
              return (
                <li key={th.id}>
                  <Link to={`/email/${th.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-accent/30">
                    <MailOpen className={th.unread ? "size-4 shrink-0 text-primary" : "size-4 shrink-0 text-muted-foreground"} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{who}</div>
                      <div className="truncate text-xs text-muted-foreground">{th.subject}</div>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{dt(th.lastMessageAt)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
